import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  DocumentIcon,
  PhotoIcon,
  VideoCameraIcon,
  PlusIcon,
  EyeIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { resourcesAPI } from '../../services/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { useToast } from '../../context/ToastContext';

interface Resource {
  _id: string;
  fileName: string;
  originalName: string;
  fileType?: string;
  mimeType: string;
  fileSize: number;
  fileSizeFormatted: string;
  uploadedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  relatedTo?: {
    objectType: string;
    objectId: string;
  };
  category: string;
  description?: string;
  tags: string[];
  isConfidential: boolean;
  accessLevel: string;
  expirationDate?: string;
  version: number;
  downloadCount: number;
  lastDownloaded?: string;
  isActive: boolean;
  isExpired?: boolean;
  daysUntilExpiration?: number;
  isImage: boolean;
  isDocument: boolean;
  isVideo: boolean;
  createdAt: string;
  updatedAt: string;
}

const ResourceList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [accessLevelFilter, setAccessLevelFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const queryClient = useQueryClient();
  const { success: showSuccess, error: showError } = useToast();

  // Fetch resources
  const {
    data: resourcesData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['resources', { 
      search: searchTerm, 
      category: categoryFilter,
      type: typeFilter,
      accessLevel: accessLevelFilter,
      page, 
      limit 
    }],
    queryFn: () => resourcesAPI.getResources({
      search: searchTerm || undefined,
      category: categoryFilter || undefined,
      type: typeFilter || undefined,
      accessLevel: accessLevelFilter || undefined,
      page,
      limit
    }),
  });

  const resources = resourcesData?.data?.data?.resources || [];
  const totalResources = resourcesData?.data?.data?.total || 0;
  const totalPages = Math.ceil(totalResources / limit);

  // Delete resource mutation
  const deleteResourceMutation = useMutation({
    mutationFn: (id: string) => resourcesAPI.deleteResource(id),
    onSuccess: () => {
      showSuccess('Resource deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['resources'] });
    },
    onError: (error: any) => {
      showError(error.response?.data?.error || 'Failed to delete resource');
    },
  });

  const handleDeleteResource = async (id: string, fileName: string) => {
    if (window.confirm(`Are you sure you want to delete "${fileName}"?`)) {
      deleteResourceMutation.mutate(id);
    }
  };

  const getFileIcon = (resource: Resource) => {
    if (resource.isImage) {
      return <PhotoIcon className="w-5 h-5 text-green-600" />;
    } else if (resource.isVideo) {
      return <VideoCameraIcon className="w-5 h-5 text-blue-600" />;
    } else {
      return <DocumentIcon className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (resource: Resource) => {
    if (resource.isExpired) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
          <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
          Expired
        </span>
      );
    } else if (resource.daysUntilExpiration && resource.daysUntilExpiration <= 30) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
          <ClockIcon className="w-3 h-3 mr-1" />
          Expires in {resource.daysUntilExpiration} days
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
          <CheckCircleIcon className="w-3 h-3 mr-1" />
          Active
        </span>
      );
    }
  };

  const getAccessLevelBadge = (accessLevel?: string) => {
    const colors = {
      public: 'bg-blue-100 text-blue-800',
      internal: 'bg-gray-100 text-gray-800',
      restricted: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${colors[accessLevel as keyof typeof colors]}`}>
        {accessLevel ? accessLevel.charAt(0).toUpperCase() + accessLevel.slice(1) : 'Unknown'}
      </span>
    );
  };

  const resetFilters = () => {
    setSearchTerm('');
    setCategoryFilter('');
    setTypeFilter('');
    setAccessLevelFilter('');
    setPage(1);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">Error loading resources. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <DocumentIcon className="w-8 h-8 text-primary-600 mr-3" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 text-left">Resources</h1>
              <p className="text-gray-600 mt-1">
                Manage documents, images, and files for clients, facilities, and placements
              </p>
            </div>
          </div>
          <Link
            to="/resources/upload"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Upload Document
          </Link>
        </div>

        {/* Filters */}
        <div className="mt-6 bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Category Filter */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Categories</option>
              <option value="Medical Records">Medical Records</option>
              <option value="Insurance Documents">Insurance Documents</option>
              <option value="Legal Documents">Legal Documents</option>
              <option value="Identification">Identification</option>
              <option value="Photos">Photos</option>
              <option value="Assessments">Assessments</option>
              <option value="Care Plans">Care Plans</option>
              <option value="Contracts">Contracts</option>
              <option value="Facility Brochures">Facility Brochures</option>
              <option value="Licenses">Licenses</option>
              <option value="Inspection Reports">Inspection Reports</option>
              <option value="Other">Other</option>
            </select>

            {/* Type Filter */}
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Types</option>
              <option value="pdf">PDF</option>
              <option value="doc">Word Document</option>
              <option value="jpg">Image (JPG)</option>
              <option value="png">Image (PNG)</option>
              <option value="txt">Text File</option>
              <option value="csv">CSV</option>
            </select>

            {/* Access Level Filter */}
            <select
              value={accessLevelFilter}
              onChange={(e) => setAccessLevelFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Access Levels</option>
              <option value="public">Public</option>
              <option value="internal">Internal</option>
              <option value="restricted">Restricted</option>
            </select>
          </div>

          {/* Filter Actions */}
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {totalResources} document{totalResources !== 1 ? 's' : ''} found
            </div>
            {(searchTerm || categoryFilter || typeFilter || accessLevelFilter) && (
              <button
                onClick={resetFilters}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Resources List */}
      {resources.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <DocumentIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Documents Found</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || categoryFilter || typeFilter || accessLevelFilter
              ? 'Try adjusting your filters or search terms.'
              : 'Get started by uploading your first document.'
            }
          </p>
          <Link
            to="/resources/upload"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Upload Document
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Document
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Uploaded By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Access
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {resources.map((resource: Resource) => (
                  <tr key={resource._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getFileIcon(resource)}
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {resource.originalName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {resource.fileType?.toUpperCase() || 'Unknown'} • Version {resource.version}
                            {resource.downloadCount > 0 && (
                              <span className="ml-2">• {resource.downloadCount} downloads</span>
                            )}
                          </div>
                          {resource.description && (
                            <div className="text-xs text-gray-400 mt-1 max-w-xs truncate">
                              {resource.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {resource.category}
                      {resource.isConfidential && (
                        <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-800">
                          Confidential
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {resource.uploadedBy?.firstName || 'Unknown'} {resource.uploadedBy?.lastName || ''}
                      <div className="text-xs text-gray-500">
                        {new Date(resource.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {resource.fileSizeFormatted}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(resource)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getAccessLevelBadge(resource.accessLevel)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center space-x-2">
                        <Link
                          to={`/resources/${resource._id}`}
                          className="text-primary-600 hover:text-primary-700"
                          title="View Details"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </Link>
                        <a
                          href={`http://localhost:3001/api/resources/${resource._id}/download`}
                          className="text-green-600 hover:text-green-700"
                          title="Download"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ArrowDownTrayIcon className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => handleDeleteResource(resource._id, resource.originalName)}
                          className="text-red-600 hover:text-red-700"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-sm text-gray-700">
                    Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalResources)} of {totalResources} results
                  </span>
                  <select
                    value={limit}
                    onChange={(e) => setLimit(Number(e.target.value))}
                    className="ml-4 px-2 py-1 border border-gray-300 rounded text-sm"
                  >
                    <option value={10}>10 per page</option>
                    <option value={20}>20 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                  </select>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ResourceList;
